import { createFileRoute, redirect } from '@tanstack/react-router';
import { headSignupSubRoute } from 'router/utils';

import { WELLHUB_USC_FLOW_FLAG } from 'hooks/useUscWellhubFlow';
import { TermsAndConditionsError } from 'pages/Opportunity/TermsAndConditions/components/TermsAndConditionsError';
import { termsAndConditionsLoader } from 'router/loaders/termsAndConditions';
import { readOppFlowFlags } from 'router/loaders/utils/oppFlowFlags';
import { isVerified } from 'services/partnersSelfSignup/otp';
import type { Country } from 'services/i18n';
import {
  getOppByIdQueryOptions,
  getOppSummaryQueryOptions,
  getVatOptionsQueryOptions,
} from 'services/partnersSelfSignup/opportunity';
import { rumService } from 'services/rum';
import { MissingTcUuidError } from 'utils/exceptions/missingTcUuidError';
import { MissingUscTcVersionError } from 'utils/exceptions/missingUscTcVersionError';
import {
  experimentVariantManager,
  updateAttributes,
} from 'utils/experimentation';

export const Route = createFileRoute(
  '/opportunity/$oppId/terms-and-conditions/',
)({
  head: headSignupSubRoute,
  beforeLoad: async ({ context, params }) => {
    const { queryClient, growthbook } = context;
    const { oppId } = params;

    const summary = await queryClient.query(
      getOppSummaryQueryOptions({ oppId }),
    );

    if (!(await isVerified({ queryClient, email: summary.oppEmail }))) {
      rumService.addWarning('terms_and_conditions_require_authentication', {
        flow: 'email_validation',
        behavior: 'redirect_to_email_validation',
        source: 'tc_route_loader',
        oppId,
        email: summary.oppEmail,
      });

      throw redirect({
        to: `/opportunity/$oppId/email-validation`,
        params: { oppId },
      });
    }

    // The first read gives the country, which only the verified Opportunity has. The flags it reads with may change once the attributes below are updated.
    const { opp: firstRead } = await queryClient.query(
      getOppByIdQueryOptions({ oppId, ...readOppFlowFlags(growthbook) }),
    );

    if (!firstRead.tcUuid) {
      const missingTcUuidError = new MissingTcUuidError(summary.oppId);

      rumService.addError(missingTcUuidError);

      throw missingTcUuidError;
    }

    // update attributes from GB since lead does not exist anymore, opp is the new source of truth
    experimentVariantManager.clear();
    // Awaited (and reported to RUM rather than thrown) since the growthbook.isOn
    // read right below is transitively country-dependent under Remote
    // Evaluation, and a genuine one-route load can afford the round trip.
    await updateAttributes({
      growthbook,
      attributes: {
        opportunity_id: firstRead.id,
        lead_id: firstRead.leadId,
        country: firstRead.countryCode,
        country_code: firstRead.countryCode.toUpperCase() as Uppercase<Country>,
      },
      source: 'tc_route_loader',
    });

    // Read again with the flags that hold now. This is a cache hit when no flag changed, and a fetch when one did, so the page renders from the entry it reads with no loading state.
    const { opp } = await queryClient.query(
      getOppByIdQueryOptions({ oppId, ...readOppFlowFlags(growthbook) }),
    );

    if (growthbook.isOn(WELLHUB_USC_FLOW_FLAG)) {
      if (!opp.uscTcVersion) {
        const missingUscTcVersionError = new MissingUscTcVersionError(
          opp.id,
          opp.countryCode,
        );

        rumService.addError(missingUscTcVersionError, {
          source: 'tc_route_loader',
          flow: 'usc',
          oppId: opp.id,
          countryCode: opp.countryCode,
        });

        throw missingUscTcVersionError;
      }

      await queryClient.ensureQueryData(
        getVatOptionsQueryOptions({
          countryCode: opp.countryCode,
          businessEmail: summary.oppEmail,
        }),
      );
    }

    return {
      queryVariables: {
        leadId: summary.oppLeadId,
        businessEmail: summary.oppEmail,
        oppId: summary.oppId,
      },
      coupon: {
        enabled: growthbook.isOn('partners.acquisition.coupon-code'),
        readonly: opp.typeAccess === 'view',
        couponCode: opp.ssuCoupon,
      },
    };
  },
  loaderDeps: ({ search }) => ({ couponCode: search.ssu_coupon }),
  loader: args => termsAndConditionsLoader(args),
  errorComponent: TermsAndConditionsError,
});
